import React, { useEffect, useState } from 'react';
import { Form, Col, Row } from 'react-bootstrap';
import { createTheme, ThemeProvider, Button, SvgIcon, Tooltip } from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { sub } from 'date-fns';
import { customValidators, object, string, date } from '../../../Helpers/Validators/customValidators';
import States from '../../../Helpers/Lists/States';
import StatesWithDisabled from '../../../Helpers/Lists/StatesWithDisabled';
import { InputMaskHeight, InputMaskWeight, InputMaskDate } from '../../../Helpers/Formatters';
import { TextInput, SelectInput, StyledRadioInput } from '../../../Helpers/Inputs';
import UserIcon from '../../../../icons/user.svg?react';
import { getBasicInfo, setBasicInfo } from '../../../../sessionStorage';
import ShieldCheckmarkIcon from '../../../../icons/shield-checkmark.svg?react';
import { calculateExactAge, calculateNearestAge } from '../../Common/Helpers/AgeCalculation';
import ModalWarning from '../../../Helpers/Widgets/ModalWarning';
import './AboutMe.scss';

export default function AboutMeForm(props) {

  const theme = createTheme({
    palette: {
      primary: {
        main: `${props.brandTheme ? props.brandTheme : "#3f51b5"}`,
      }
    },
    typography: {
      fontFamily: "museo-sans",
      body1: {
        fontSize: 14,
        fontWeight: 500,
      },
    },
    components: {
      MuiInputBase: {
        styleOverrides: {
          input: {
            fontSize: 16,
          },
          root: {
            height: 56,
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            backgroundColor: "rgba(0, 0, 0, 0.05)",
          },
          underline: {
            "&::before": {
              borderBottom: "solid 2px #b2b2b2",
            },
            "&:hover::before": {
              borderBottomWidth: "2px",
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontSize: 16,
            color: "rgba(0,0,0,0.6)",
          }
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            marginTop: 0,
            paddingTop: 0,
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          shrink: {
            color: "rgba(0,0,0,0.3) !important",
          },
        },
      },
      MuiNativeSelect: {
        styleOverrides: {
          fontSize: 16,
          icon: {
            color: "rgba(0,0,0,1)"
          },
          iconFilled: {
            right: 16,
          },
        },
      },
    },
  });

  const basicInfo = getBasicInfo();
  const [formState, setFormState] = useState(AboutMeForm.DefaultFormState(false, basicInfo));

  const [purlMismatch, setPurlMismatch] = useState(false);
  const [initialBasicInfo] = useState(basicInfo);

  const [tooltip, setTooltip] = useState(false);
  const handleTooltipAboutClose = () => {
    setTooltip(false);
  };
  const handleTooltipAboutOpen = () => {
    setTooltip(true);
  };

  const formMethods = useForm({
    resolver: yupResolver(AboutMeForm.ValidationSchema(props.ageSpecs)),
    mode: 'all',
    reValidateMode: 'all',
    defaultValues: formState,
  });
  const { handleSubmit, trigger } = formMethods;

  useEffect(() => {
    (async () => {
      if (props.isPurlVerification && props.selectedQuote && !initialBasicInfo.birthdate) {
        let mismatch = false;
        if (formState.birthdate !== '' && (await trigger('birthdate'))) {
          let initialAge, newAge;
          if (props.selectedQuote.ageDetermination && props.selectedQuote.ageDetermination.toLowerCase() === 'nearest') {
            initialAge = calculateNearestAge(new Date(initialBasicInfo.fakeBirthdate));
            newAge = calculateNearestAge(new Date(formState.birthdate));
          } else {
            initialAge = calculateExactAge(new Date(initialBasicInfo.fakeBirthdate));
            newAge = calculateExactAge(new Date(formState.birthdate));
          }
          mismatch = mismatch || initialAge !== newAge;
        }
        mismatch = mismatch || initialBasicInfo.gender !== formState.gender;
        mismatch = mismatch || initialBasicInfo.state !== formState.state;
        mismatch = mismatch || initialBasicInfo.tobacco !== formState.tobacco;
        setPurlMismatch(mismatch);
      }
    })();
  }, [formState]);

  const handleValueChange = (field, value) => {
    setFormState({ ...formState, [field]: value });
    if (props.onFieldChange) {
      props.onFieldChange(field, value);
    }
  };

  function calculateTotalHeight() {
    const radix = 10;
    var heightArray = formState.height.split(" ")
    var heightFeet = parseInt(heightArray[0], radix);
    var heightInches;
    for (var i = 1; i < heightArray.length; i++) {
      if (heightArray[i] !== "" && heightArray[i] !== "ft") {
        heightInches = parseInt(heightArray[i], radix);
        break;
      }
    };
    return heightFeet * 12 + heightInches;
  }

  const onSubmit = () => {

    const totalHeight = calculateTotalHeight();
    var weight = formState.weight.split(" ")[0];

    if (props.validateBMI != null && props.validateBMI(totalHeight, formState.bmi, weight) && props.onDeclined != null) {
      props.onDeclined();
    } else {
      // Save formState to basicinfo
      var basicInfoFromSessionStorage = getBasicInfo();
      basicInfoFromSessionStorage.gender = formState.gender;
      basicInfoFromSessionStorage.birthdate = formState.birthdate;
      basicInfoFromSessionStorage.state = formState.state;
      basicInfoFromSessionStorage.height = formState.height;
      basicInfoFromSessionStorage.weight = formState.weight;
      basicInfoFromSessionStorage.email = formState.email;
      basicInfoFromSessionStorage.tobacco = formState.tobacco;
      basicInfoFromSessionStorage.usResident = formState.usResident;

      setBasicInfo(basicInfoFromSessionStorage);

      if (props.openEditPopUp) {
        props.setOpenEditPopUp(false, purlMismatch);
      } else {
        props.onNext();
      }
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <div className="basic-info-about-me d-flex justify-content-center">
        <FormProvider {...formMethods}>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row className="justify-content-center" >
              <Col lg={12}>
                {props.showHeader && (
                  <React.Fragment>
                    <Row className="justify-content-center" >
                      <SvgIcon color='primary' component={UserIcon} className="icons" fontSize="large" />
                    </Row>
                    <Row className="justify-content-center" >
                      <h3 className="py-3">Profile</h3>
                      <Tooltip
                        title="This information should represent the person who will be insured and covered by the policy, whether that's you or someone else."
                        id="about-tooltip"
                        aria-describedby="about-tooltip"
                        aria-label="more about sort by price"
                        className="toolInfo"
                        role="tooltip"
                        enterTouchDelay={0}
                        leaveTouchDelay={5000}
                        onClose={() => handleTooltipAboutClose()}
                        open={tooltip}
                        slotProps={{
                          tooltip: {
                            className: 'light-tooltip toolInfo',
                            sx: { fontSize: '14px' }
                          },
                        }}
                      >
                        <button type='button' id="about" role="tooltip" className="quote-tooltip" onMouseEnter={() => handleTooltipAboutOpen()} onClick={() => handleTooltipAboutOpen()} tabIndex="0" aria-disabled="true" aria-describedby="about"><InfoIcon color="action"></InfoIcon></button>
                      </Tooltip>
                    </Row>
                  </React.Fragment>
                )}

                <StyledRadioInput
                  name="gender"
                  id="gender"
                  value={formState.gender}
                  options={[{ value: "F", label: "Female", icon: 'female' }, { value: "M", label: "Male", icon: 'male' }]}
                  onChange={e => handleValueChange('gender', e)}
                  isBlankIcon={true}
                  isBlankCheckMark={true}
                />

                <div className="mb-3">
                  <TextInput
                    name="birthdate"
                    id="birthdate"
                    label="Date of Birth"
                    value={formState.birthdate}
                    onChange={(e) => handleValueChange('birthdate', e.target.value)}
                    InputProps={{
                      inputComponent: InputMaskDate,
                    }}
                  />
                </div>

                <div className="mb-3">
                  <SelectInput
                    name="state"
                    id="state"
                    value={formState.state}
                    onChange={(e) => handleValueChange('state', e.target.value)}
                    label="State"
                    selectOptions={props.unavailableStates ? StatesWithDisabled(props.unavailableStates) : States()}
                  />
                </div>

                <div className="mb-3">
                  <SelectInput
                    name="tobacco"
                    id="tobacco"
                    value={formState.tobacco}
                    onChange={(e) => handleValueChange('tobacco', e.target.value)}
                    label="Tobacco/Nicotine Use"
                    hideEmptyOption
                    selectOptions={[
                      { value: 'Within the last year', label: 'Within the last year' },
                      { value: 'Within the last 1 to 3 years', label: 'Within the last 1 to 3 years' },
                      { value: 'Within the last 3 to 5 years', label: 'Within the last 3 to 5 years' },
                      { value: 'Over 5 years ago', label: 'Over 5 years ago' },
                      { value: 'Never', label: 'Never' },
                    ]}
                  />
                </div>

                <Row>
                  <Col className="ppe-10_5">
                    <div className="mb-3">
                      <TextInput
                        name="height"
                        id="height"
                        label="Height"
                        value={formState.height}
                        onChange={(e) => handleValueChange('height', e.target.value)}
                        InputProps={{
                          inputComponent: InputMaskHeight,
                        }}
                      />
                    </div>
                  </Col>

                  <Col className="pps-10_5">
                    <div className="mb-3">
                      <TextInput
                        name="weight"
                        id="weight"
                        label="Weight"
                        value={formState.weight}
                        onChange={(e) => handleValueChange('weight', e.target.value)}
                        InputProps={{
                          inputComponent: InputMaskWeight,
                        }}
                      />
                    </div>
                  </Col>
                </Row>
                {purlMismatch &&
                  <Row>
                    <Col>
                      <ModalWarning message='Your quotes have been refreshed to reflect the information you provided. Take a moment to review the updated results and choose the product that best aligns with your needs.' />
                    </Col>
                  </Row>
                }
              </Col>
            </Row>

            <Row className="mt-0">
              {!props.submitButtonVariant &&
                <Col className="text-end pb-3">
                  <Button variant="contained" size="medium" color="primary" type="submit">
                    NEXT
                  </Button>
                </Col>
              }
              {props.submitButtonVariant && props.submitButtonVariant === 'landing' &&
                <Col className="d-flex m-0">
                  <Button variant="contained" color="primary" type="submit" fullWidth className='submit-button'> <ShieldCheckmarkIcon className="button-icon" /> QUICK QUOTE</Button>
                </Col>
              }
              {props.submitButtonVariant && props.submitButtonVariant === 'purl' &&
                <Col className="d-flex m-0">
                  <Button variant="contained" size="medium" color="primary" type="submit" fullWidth className='submit-button'>{purlMismatch ? "BACK TO RESULTS" : "CONTINUE"}</Button>
                </Col>
              }
            </Row>
            {props.quickQuoteDisclaimer && props.submitButtonVariant === 'landing' &&
              <Row>
                <Col className="quick-quote-disclaimer-container" dangerouslySetInnerHTML={{ __html: props.quickQuoteDisclaimer }}></Col>
              </Row>
            }
          </Form>
        </FormProvider>
      </div>
    </ThemeProvider>
  )
}

AboutMeForm.ValidationSchema = (ageSpecs) => {
  return object().shape({
    gender: string().required('Gender is required.'),
    birthdate: date()
      .typeError('Invalid date.')
      .max(sub(new Date(), { years: ageSpecs.min, days: ageSpecs.minAgeDays || 0 }), `Minimum age is ${ageSpecs.minAgeDays ? `${ageSpecs.minAgeDays} days` : ageSpecs.min}.`)
      .min(sub(new Date(), { years: ageSpecs.max + 1 }), `Maximum age is ${ageSpecs.max}.`)
      .required('Birthdate is required.'),
    state: string().required('State is required.'),
    height: customValidators.string()
      .required('Height is required.')
      .isValidEtradeHeightString('Invalid height', 36, 84),
    weight: customValidators.string()
      .required('Weight is required.')
      .isValidWeightString('Please verify your weight has been entered correctly.', 78, 395),
    tobacco: string().required('This field is required.'),
    email: string().notRequired().email('Invalid email.').nullable(),
  })
};

AboutMeForm.DefaultFormState = (prefill = false, basicInfo = null) => {
  return {
    gender: prefill ? 'M' : (basicInfo && basicInfo.gender) || '',
    birthdate: prefill ? '11/15/1980' : (basicInfo && basicInfo.birthdate) || '',
    tobacco: prefill ? 'Never' : (basicInfo && basicInfo.tobacco) || 'Never',
    state: prefill ? 'ID' : (basicInfo && basicInfo.state) || '',
    height: prefill ? '5 ft 10 in' : (basicInfo && basicInfo.height) || '',
    weight: prefill ? '175 pounds' : (basicInfo && basicInfo.weight) || '',
    email: prefill ? 'user@address.com' : (basicInfo && basicInfo.email) || '',
    usResident: true,
    bmi: (basicInfo && basicInfo.bmi) || undefined,
  };
};
